import { useEffect, useRef, useCallback } from 'react';
import {
  HubConnectionState,
} from '@microsoft/signalr';
import { logger } from '@/lib/logger';
import type {
  DocumentErrorNotification,
  DocumentLogEntry,
  DocumentProgressUpdate,
  DocumentStatusChangedEvent,
} from '@/types/document';
import {
  getOrCreateHubConnection,
  ensureHubConnected,
  incrementSubscribers,
  decrementAndMaybeDisconnect,
  getSharedConnection,
} from './hubConnection';

interface DocumentHubCallbacks {
  onProgressUpdate?: (data: DocumentProgressUpdate) => void;
  onStatusChanged?: (data: DocumentStatusChangedEvent) => void;
  onLogEntry?: (data: DocumentLogEntry) => void;
  onError?: (data: DocumentErrorNotification) => void;
}

/**
 * Hook to manage SignalR connection to DocumentProcessingHub.
 * Uses a singleton connection shared across all component instances.
 * Only connects when documentId is provided (not null).
 */
export function useDocumentHub(
  documentId: string | null,
  callbacks: DocumentHubCallbacks,
) {
  // Keep callbacks in a ref so event handlers never go stale
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const subscribedDocRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't connect if there's no document to watch
    if (!documentId) return;

    let cancelled = false;
    const connection = getOrCreateHubConnection();
    incrementSubscribers();

    // Bound handlers that delegate to latest callbacks via ref
    const onProgress = (data: DocumentProgressUpdate) => {
      if (!cancelled) cbRef.current.onProgressUpdate?.(data);
    };
    const onStatus = (data: DocumentStatusChangedEvent) => {
      if (!cancelled) cbRef.current.onStatusChanged?.(data);
    };
    const onLog = (data: DocumentLogEntry) => {
      if (!cancelled) cbRef.current.onLogEntry?.(data);
    };
    const onError = (data: DocumentErrorNotification) => {
      if (!cancelled) cbRef.current.onError?.(data);
    };

    connection.on('ProgressUpdate', onProgress);
    connection.on('StatusChanged', onStatus);
    connection.on('LogEntry', onLog);
    connection.on('Error', onError);

    // Connect + subscribe
    ensureHubConnected(connection)
      .then(() => {
        if (!cancelled && connection.state === HubConnectionState.Connected) {
          return connection.invoke('SubscribeToDocument', documentId);
        }
      })
      .then(() => {
        if (!cancelled) {
          subscribedDocRef.current = documentId;
          logger.debug('DocumentHub', `Subscribed to ${documentId}`);
        }
      })
      .catch((err) => {
        if (!cancelled) logger.error('DocumentHub', 'Error', err);
      });

    return () => {
      cancelled = true;

      // Remove THIS component's handlers
      connection.off('ProgressUpdate', onProgress);
      connection.off('StatusChanged', onStatus);
      connection.off('LogEntry', onLog);
      connection.off('Error', onError);

      // Unsubscribe from document group
      if (subscribedDocRef.current && connection.state === HubConnectionState.Connected) {
        connection.invoke('UnsubscribeFromDocument', subscribedDocRef.current).catch(() => {});
        subscribedDocRef.current = null;
      }

      decrementAndMaybeDisconnect();
    };
  }, [documentId]);

  const conn = getSharedConnection();
  return {
    subscribe: useCallback((docId: string) => {
      if (!conn || conn.state !== HubConnectionState.Connected)
        return Promise.reject('No connection');
      return conn.invoke('SubscribeToDocument', docId);
    }, [conn]),
    unsubscribe: useCallback((docId: string) => {
      if (!conn || conn.state !== HubConnectionState.Connected)
        return Promise.reject('No connection');
      return conn.invoke('UnsubscribeFromDocument', docId);
    }, [conn]),
  };
}
